import { GUID, sha256 } from 'core/utils';
import FireStoreClass from '@FireStore';
import { httpPost } from 'core/utils/httpClient';
import { getReduxStore } from '@Common/redux/store';
import { GenericObject, NativePlatform } from 'core/models/platform';
import getConfig from 'next/config';
import Bugsnag from '@bugsnag/js';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { getKlaviyoInstance } from '../libs/klaviyo';

const { publicRuntimeConfig } = getConfig();

interface IStoreActionData {
  userID: string | undefined | null;
  action: string;
  value: GenericObject;
  origin?: string;
  build: string;
}

class Track {
  private static instance: Track;

  private quillSessionID: string = GUID();

  private marketingEventSent = false;

  lang: string;

  appMode: 'Mobile' | 'Extension' | 'Desktop' = 'Desktop';

  trackData: Array<IStoreActionData>;

  sendQuantity: number;

  isSending: boolean;

  dataSenderActive: boolean;

  // ip = '';

  // countryCode = '';

  // locationDataPromise: Promise<IpData | null | void> | null = null;

  // locationData: IpData | null = null;

  chromeExtHost = '';

  locationRetryCounter = 0; // Location data retry counter

  // private ipApiCount = 0;

  constructor() {
    // amplitude.getInstance().init(publicRuntimeConfig.AMPLITUDE_API_KEY);
    this.lang = getReduxStore()?.getState()?.common?.language || '';
    const platform = getReduxStore()?.getState()?.common?.platform || {};
    if (platform.isMobile) {
      this.appMode = 'Mobile';
    } else if (platform.isExtension === true) {
      this.appMode = 'Extension';
    } else {
      this.appMode = 'Desktop';
    }
    this.trackData = [];
    this.sendQuantity = 50;
    this.isSending = false;
    this.dataSenderActive = false;

    // export const getIpData = (): Promise<IpData> =>
    // this.setIpData();
    // QuillEventEmitter.getInstance().on('IP_DATA_EVENT', (data: IpData) => {
    //   this.locationData = data;
    // });
  }

  private getAppMode = (): string => {
    const platform = getReduxStore()?.getState()?.common?.platform || {};
    if (platform.isExtension === true) {
      return 'Extension';
    }
    if (platform.isMobile) {
      return 'Mobile';
    }
    if (platform.native === NativePlatform.ANDROID) {
      return 'AndroidApp';
    }
    return 'Desktop';
  };

  private getExtensionType = (): string | null | undefined => {
    const platform = getReduxStore()?.getState()?.common?.platform || {};
    if (platform.isExtension) return platform.extensionMetaData?.type;
    return null;
  };

  setChromeExtHost = (url: string) => {
    this.chromeExtHost = url;
  };

  static getInstance = (): Track => {
    if (!Track.instance) Track.instance = new Track();
    return Track.instance;
  };

  private getCommonData = () => {
    let page = '/paraphraser';
    const pageRoute = window?.location.pathname === '/' ? page : window?.location.pathname;
    page = pageRoute.substr(1)[0].toUpperCase() + pageRoute.substr(2);
    if (page === 'Summarize') {
      page = 'Summarizer';
    } else if (page === 'Grammar-check') {
      page = 'Grammar-checker';
    } else if (page.toLowerCase().includes('co-writer')) {
      page = 'Co-writer';
    }

    const Firestore = FireStoreClass.getInstance();
    const premium = Firestore?.isPremiumUser() || false;
    const authenticated = Firestore?.authenticated || false;
    const extensionType = this.getExtensionType();
    const extensionData = extensionType ? { extensionType } : {};
    const chromeHostData =
      extensionType === 'chrome' && this.chromeExtHost ? { hostOrigin: this.chromeExtHost } : {};
    const partnerCompany = getReduxStore()?.getState()?.common?.partnerCompany || '';
    const productDomainName = partnerCompany?.length ? { productDomain: partnerCompany } : {};

    return {
      page,
      premium,
      authenticated,
      appMode: this.getAppMode(),
      product: page,
      ...extensionData,
      ...chromeHostData,
      ...productDomainName,
    };
  };

  private getAffiliateData = (eventName: string, data: GenericObject) => {
    const affiliation = data.affiliation;
    switch (eventName) {
      case 'signupSuccess':
        return {
          sku: 'SIGNUP',
          orderId: GUID(),
        };

      case 'boughtPremium':
        if (affiliation && typeof affiliation === 'string' && affiliation !== '') {
          return {
            sku: affiliation.toUpperCase(),
            orderId: GUID(),
          };
        }
        return {};

      default:
        return {};
    }
  };

  storeActionEvent = async (eventName: string, eventData = {}) => {
    if (QuillLocalStorage.getInstance().get('userTrackingAllowed', 'true') === 'false') return;

    // await this.setIpData();

    /* if (this.locationData === null) {
      const locationData = getReduxStore()?.getState()?.common?.ipData;
      if (typeof locationData === 'object' && Object.keys(locationData).length) {
        this.locationData = locationData;
      } else if (this.locationRetryCounter < 3) {
        // Retyring mechansim for max 3 storeActionEvent call if locationData is not present.
        setTimeout(() => {
          this.locationRetryCounter += 1;
          this.storeActionEvent(eventName, eventData);
        }, 400);
        return;
      }
    } */

    const Firestore = FireStoreClass.getInstance();
    const userID = Firestore.getUsername();
    /* const location = {
      city: this.locationData?.city,
      countryCode: this.locationData?.countryCode,
      postal: this.locationData?.postal,
      regionCode: this.locationData?.regionCode,
    }; */
    this.trackData.push({
      userID,
      action: eventName,
      value: { ...eventData, ...this.getCommonData(), eventTime: Math.floor(Date.now() / 1000) },
      build: publicRuntimeConfig.APP_VERSION,
    });
    if (!this.dataSenderActive) {
      this.dataSenderActive = true;
      this.sendActionEvent();
    }
  };

  private sendActionEvent = () => {
    setInterval(() => {
      if (this.trackData.length > 0) {
        const trackDataToBeSend = this.trackData.splice(0);
        while (trackDataToBeSend.length) {
          const postData = trackDataToBeSend.splice(0, this.sendQuantity);
          httpPost('/tracking', { data: postData }).catch(() => {
            // only try once if api is failed
            httpPost('/tracking', { data: postData }).catch(() => {
              Bugsnag.notify({
                name: 'TRACKING_SEND_ERROR',
                message: 'Tracking data was not sent successfully by the user. Retrying.',
              });
            });
          });
        }
      }
    }, 400);
  };

  sendFeedback = async (feedback: { name: string; email: string; message: string }) => {
    const Firestore = FireStoreClass.getInstance();
    const userID = Firestore.getUsername();
    const postData: { data: IStoreActionData } = {
      data: {
        userID,
        action: 'feedback',
        value: {
          ...feedback,
          ...this.getCommonData(),
          consoleLogs: console.logs.length ? JSON.stringify(console.logs) : '',
        },
        build: publicRuntimeConfig.APP_VERSION,
      },
    };
    return httpPost('/feedback', postData);
  };

  gaEvent = (
    category: string,
    action: string,
    label?: string | number | null | Record<string, unknown>,
    value?: string | number | boolean
  ) => {
    if (window && window.ga) {
      if (label) {
        if (value) {
          window.ga('send', 'event', category, action, label, value);
        } else {
          window.ga('send', 'event', category, action, label);
        }
      } else {
        window.ga('send', 'event', category, action);
      }
    }
  };

  gTagEvent = async (eventName: string, data: GenericObject = {}) => {
    const Firestore = FireStoreClass.getInstance();
    if (Firestore) {
      data.userID = Firestore.getUsername();
      data.premium = Firestore.isPremiumUser();
    }
    data = {
      ...data,
      ...this.getCommonData(),
      ...this.getAffiliateData(eventName, data),
      sha256User: {
        email: await sha256(Firestore?.user?.email as string, 'hashedEm'),
      },
    };

    window.dataLayer?.push({
      event: eventName,
      ...data,
    });
  };

  ampSetOnce = (category: string, value: number | string | boolean) => {
    if (window && window.amplitude) {
      const identify = new window.amplitude.Identify().setOnce(category, value);
      window.amplitude.getInstance().identify(identify);
    } else {
      setTimeout(() => {
        this.ampSetOnce(category, value);
      }, 300);
    }
  };

  ampAdd = (category: string) => {
    if (window && window.amplitude) {
      const identify = new window.amplitude.Identify().add(category, 1);
      window.amplitude.getInstance().identify(identify);
    } else {
      setTimeout(() => {
        this.ampAdd(category);
      }, 300);
    }
  };

  ampSetUserProperties = (userPropertiesData: GenericObject) => {
    if (window && window.amplitude) {
      const indentify = new window.amplitude.Identify();
      const keys = Object.keys(userPropertiesData);
      if (keys && keys.length > 0) {
        for (let i = 0; i < keys.length; i += 1) {
          const key = keys[i];
          indentify.set(key, userPropertiesData[key]);
        }
        window.amplitude.getInstance().identify(indentify);
      }
    } else {
      setTimeout(() => {
        this.ampSetUserProperties(userPropertiesData);
      }, 300);
    }
    // set extension used with user properties
    const extType = this.getExtensionType();
    if (extType) {
      const categoryExtmap: Record<string, string> = {
        chrome: 'usedChromeExt',
        'google-docs': 'usedDocsExt',
        'ms-word': 'usedWordExt',
      };
      this.ampSetOnce(categoryExtmap[extType], true);
    }
  };

  ampEvent = (eventName: string, data = {}) => {
    const allData = { ...data, ...this.getCommonData() };
    if (window && window.amplitude) {
      window.amplitude.getInstance().logEvent(eventName, allData);
      if (window.location.pathname !== '/co-writer') {
        getKlaviyoInstance().track(eventName, allData);
      }
    } else {
      setTimeout(() => {
        this.ampEvent(eventName, data);
      }, 300);
    }
    // Simply Passing all events to be sent to Amptitude to Klaviyo as well. Klaviyo will send events only if the user is authenticated.
  };

  sendAllEvents = (eventName: string, data = {}) => {
    this.ampEvent(eventName, data);
    this.storeActionEvent(eventName, data);
  };

  setUserId = (userId: string | null, quId: string | null) => {
    this.gaEvent('set', 'userId', userId);
    if (window && window.ga) window.ga('set', 'dimension3', quId);
    window.amplitude?.getInstance()?.setUserId(userId);
  };

  premiumLinkClicked = (position: string) => {
    this.gaEvent('premiumLinks', 'premiumLinkClicked', position);
    this.gTagEvent('premiumLinkClicked', { position });
    this.ampEvent('premiumLinkClicked', { position });
  };

  upgradeLinkClicked = (position: string) => {
    this.gaEvent('upgradeLinks', 'upgradeLinkClicked', position);
    this.gTagEvent('upgradeLinkClicked', { position });
    this.ampEvent('upgradeLinkClicked', { position });
  };

  signupLinkClicked = (position: string) => {
    this.gaEvent('signupLinks', 'signupLinkClicked', position);
    this.gTagEvent('signupLinkClicked', { position });
    this.ampEvent('signupLinkClicked', { position });
  };

  // set marketing campaign event and add to user property
  triggerMarktingEvents = () => {
    // don't send the marketing event if it is already sent; case => user navigates from one page to another
    if (this.marketingEventSent) return;
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('utm_source') || '';
    const medium = urlParams.get('utm_medium') || '';
    const name = urlParams.get('utm_name') || '';
    const campaign = urlParams.get('utm_campaign') || '';
    if (!source && !medium) {
      return;
    }
    this.ampSetUserProperties({ source, medium, name, campaign });
    this.ampAdd('marketingAttributionCount');
    this.ampEvent('marketingAttribution', { source, medium, name, campaign });
    this.marketingEventSent = true;
  };

  visitedExitSurvey = (
    deviceID: string | string[] | undefined,
    chromeExtVersion: string | string[] | undefined
  ) => {
    if (deviceID) {
      this.storeActionEvent('chrome-active/user/uninstalled-chrome-ext', {
        deviceID,
        chromeExtVersion,
      });
    }
  };

  visitedPremium = (referrer: string) => {
    const data: { referrer?: string } = {};
    if (referrer) {
      data.referrer = referrer;
    }
    this.storeActionEvent('cart-visited', data);
    this.storeActionEvent('visitedPremium', data);
    // ga('set', 'metric6', '1');
    this.gaEvent('ec:setAction', 'checkout', { step: 1 });
    this.ampAdd('visitedPremium');
    this.ampEvent('visitPremium', data);
    this.gTagEvent('visitedPremium', data);
    this.triggerMarktingEvents();
  };

  visitedPage = (pageRoute: string, referrer = '') => {
    if (pageRoute === '/premium') {
      this.visitedPremium(referrer);
      return;
    }
    const mapping: Record<string, string> = {
      '/citation-generator': 'CitationGenerator',
      '/grammar-check': 'GrammarChecker',
      '/co-writer': 'CoWriter',
      '/co-writer/home': 'CWEditor',
      '/summarize': 'Summarizer',
      '/': 'Paraphraser',
      '/careers': 'CareersPage',
      '/about': 'TeamPage',
      '/contact': 'ContactUsPage',
      '/upgrade': 'UpgradePage',
      '/hotkeys': 'HotKeysPage',
      '/account': 'UserAccountsPage',
      '/terms': 'TermsOfService',
      '/privacy': 'PrivacyPolicy',
      // '/api-access': 'ApiAccessPage',
      '/login': 'LoginPage',
      '/signup': 'SignupPage',
      '/recovery': 'ForgotPasswordPage',
      '/word': 'WordInstallationHelpPage',
      '/affiliates': 'AffiliatePage',
      '/try': 'TryPage',
      '/workshop': 'WorkshopPage',
      '/workshopSuccess': 'WorkshopSuccessPage',
      '/exit-survey': 'ExitSurveyPage',
      '/extension-success': 'ExtensionSucessPage',
      '/word-counter': 'WordCounterPage',
      '/plagiarism-checker': 'PlagiarismChecker',
    };

    if (mapping[pageRoute]) {
      const eventName = `visited${mapping[pageRoute]}`;
      if (referrer) {
        this.storeActionEvent(eventName, { referrer });
        this.ampEvent(eventName, { referrer });
      } else {
        this.storeActionEvent(eventName);
        this.ampEvent(eventName);
      }
    }
    this.triggerMarktingEvents();
  };

  shownSection = (section: string) => {
    const mapping: Record<string, string> = {
      personalize: 'PersonalizeQuestions',
      welcome: 'WelcomeAfterRegistration',
      paraphraserLimitSignupModal: 'paraphraserLimitSignupModal',
      premiumDialog: 'PremiumModal',
    };
    if (mapping[section]) {
      const eventName = `shown${mapping[section]}`;
      this.storeActionEvent(eventName);
      this.ampEvent(eventName);
    }
  };
}
export default Track;
